import { useEffect } from 'react';
import {
  CheckCircle2,
  Pause,
  Play,
  RotateCcw,
  Volume2,
  VolumeX,
  XCircle,
} from 'lucide-react';

import type { MediaSource } from '@/entities/course/model/dto';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/utils';

import { useDescriptionAudioPlayer } from '../model/use-description-audio-player';

const DESCRIPTION_AUTO_ADVANCE_FALLBACK_DELAY_MS = 2_000;

const formatTimestamp = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const wholeSeconds = Math.floor(seconds);
  const mins = Math.floor(wholeSeconds / 60)
    .toString()
    .padStart(2, '0');
  const secs = Math.floor(wholeSeconds % 60)
    .toString()
    .padStart(2, '0');
  return `${mins}:${secs}`;
};

interface DescriptionModalProps {
  open: boolean;
  answer: boolean | null;
  isCorrect: boolean | null;
  remainingCount: number;
  questionTitle: string;
  questionDescription: string;
  explanationSource: MediaSource | null;
  onOpenChange: (open: boolean) => void;
  onContinue: () => void | Promise<void>;
}

export const DescriptionModal = ({
  open,
  answer,
  isCorrect,
  remainingCount,
  questionTitle,
  questionDescription,
  explanationSource,
  onOpenChange,
  onContinue,
}: DescriptionModalProps) => {
  const {
    audioRef,
    state,
    controls,
    hasSource: hasExplanationAudio,
  } = useDescriptionAudioPlayer(explanationSource);

  useEffect(() => {
    if (!open) {
      controls.pause();
      controls.seekTo(0);
      return;
    }

    if (hasExplanationAudio) {
      controls.play();
    }
  }, [open, controls, hasExplanationAudio]);

  useEffect(() => {
    if (!open) return;
    if (!hasExplanationAudio) return;
    if (!state.isEnded) return;

    if (remainingCount > 0 || answer === null) {
      onOpenChange(false);
      void onContinue();
    }
  }, [
    answer,
    hasExplanationAudio,
    onContinue,
    onOpenChange,
    open,
    remainingCount,
    state.isEnded,
  ]);

  useEffect(() => {
    if (!open) return;
    if (answer !== null) return;
    if (hasExplanationAudio) return;

    const timer = setTimeout(() => {
      onOpenChange(false);
      void onContinue();
    }, DESCRIPTION_AUTO_ADVANCE_FALLBACK_DELAY_MS);

    return () => clearTimeout(timer);
  }, [answer, hasExplanationAudio, onContinue, onOpenChange, open]);

  const handleContinue = () => {
    onOpenChange(false);
    void onContinue();
  };

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-start justify-center px-4 pt-36 pb-3 transition-opacity duration-200 ease-out',
        open ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="quiz-description-modal-title"
      aria-describedby="quiz-description-modal-description"
    >
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />

      <div
        className={cn(
          'relative z-10 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.2)] transition-all duration-200 ease-out',
          open ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
        )}
        role="document"
      >
        <header className="space-y-2">
          <h2
            id="quiz-description-modal-title"
            className="text-lg font-semibold text-slate-900"
          >
            {questionTitle}
          </h2>
        </header>

        <div className="mt-5 space-y-5">
          <div className="flex items-center rounded-2xl bg-slate-50 p-4 gap-4">
            <div
              className={cn(
                'flex  items-center justify-center rounded-full',
                isCorrect
                  ? 'bg-emerald-500 text-white'
                  : 'bg-rose-500 text-white',
              )}
            >
              {isCorrect ? (
                <CheckCircle2 className="h-6 w-6" />
              ) : (
                <XCircle className="h-6 w-6" />
              )}
            </div>
            <div className="space-y-1 text-sm text-slate-600">
              <p className="font-medium text-slate-500">
                {answer === null
                  ? 'No answer'
                  : `Your answer: ${answer ? 'YES' : 'NO'}`}
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700">
                Answer Explanation Audio
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 rounded-full"
                  onClick={() =>
                    state.isPlaying ? controls.pause() : controls.play()
                  }
                  disabled={!hasExplanationAudio}
                  aria-label={
                    state.isPlaying
                      ? 'Pause explanation audio'
                      : 'Play explanation audio'
                  }
                >
                  {state.isPlaying ? (
                    <Pause className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 rounded-full"
                  onClick={controls.toggleMute}
                  disabled={!hasExplanationAudio}
                  aria-label={
                    state.isMuted
                      ? 'Unmute explanation audio'
                      : 'Mute explanation audio'
                  }
                >
                  {state.isMuted ? (
                    <VolumeX className="h-4 w-4" />
                  ) : (
                    <Volume2 className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 rounded-full"
                  onClick={controls.restart}
                  disabled={!hasExplanationAudio}
                  aria-label="Play explanation audio from start"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {hasExplanationAudio ? (
              <>
                <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                  <span>{formatTimestamp(state.currentTime)}</span>
                  <span>{formatTimestamp(state.duration)}</span>
                </div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-[width] duration-150 ease-out"
                    style={{ width: `${state.progressPercent}%` }}
                  />
                </div>
              </>
            ) : (
              <p className="mt-3 text-xs text-slate-500">
                The explanation audio for this question is not yet available.
              </p>
            )}
          </div>
        </div>

        <footer className="mt-6">
          <Button
            className="w-full rounded-xl bg-slate-900 text-sm font-semibold text-white hover:bg-slate-800 h-30"
            onClick={handleContinue}
          >
            {remainingCount > 0 ? 'Next Question' : 'Go to grading'}
          </Button>
        </footer>

        <audio ref={audioRef} className="hidden" />
      </div>
    </div>
  );
};
